import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { FileText, Upload, Trash2, Download, Loader2, Eye, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function InvoiceUploader({ orderId, orderType = "b2b", token, canUpload = false }) {
  const [invoices, setInvoices] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [title, setTitle] = useState('Invoice');

  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/order-invoices/${orderId}`);
      setInvoices(res.data || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { if (orderId) fetchInvoices(); }, [orderId]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('order_id', orderId);
      formData.append('order_type', orderType);
      formData.append('title', title);
      formData.append('file', file);
      await axios.post(`${API_URL}/api/order-invoices/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Invoice uploaded');
      setShowUpload(false);
      setTitle('Invoice');
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await axios.delete(`${API_URL}/api/order-invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch { toast.error('Delete failed'); }
  };

  if (invoices.length === 0 && !canUpload) return null;

  return (
    <div className="mt-3" data-testid="invoice-section">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-xs font-medium text-slate-600">Invoices ({invoices.length})</span>
        {canUpload && (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => setShowUpload(true)}>
            <Upload className="w-3 h-3 mr-1" /> Upload
          </Button>
        )}
      </div>
      {invoices.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {invoices.map(inv => (
            <div key={inv.id} className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-md px-2 py-1 text-xs">
              <FileText className="w-3 h-3 text-blue-600" />
              <span className="text-blue-800 max-w-[120px] truncate">{inv.title || inv.file_name}</span>
              <a href={`${API_URL}${inv.file_url}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800">
                <Eye className="w-3 h-3" />
              </a>
              <a href={`${API_URL}${inv.file_url}`} download className="text-blue-600 hover:text-blue-800">
                <Download className="w-3 h-3" />
              </a>
              {canUpload && (
                <button onClick={() => handleDelete(inv.id)} className="text-red-400 hover:text-red-600">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Upload Invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Invoice" className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">File (PDF or Image)</Label>
              <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                className="block w-full text-sm text-slate-500 file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                data-testid="invoice-file-input" />
            </div>
            {uploading && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ReviewSection({ productId, vendorId, productType = "medicine", canReview = false, canReply = false, token }) {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ average_rating: 0, total_reviews: 0, distribution: {} });
  const [showForm, setShowForm] = useState(false);
  const [editReview, setEditReview] = useState(null);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  const fetchReviews = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reviews/product/${productId}?vendor_id=${vendorId}`);
      setReviews(res.data.reviews || []);
      setSummary({ average_rating: res.data.average_rating, total_reviews: res.data.total_reviews, distribution: res.data.distribution || {} });
    } catch (err) { console.error(err); }
  };

  // Lazy-load reviews only once this section scrolls near the viewport, so a catalog
  // of hundreds of products doesn't fire hundreds of simultaneous requests on page load.
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '300px' });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => { if (productId && isVisible) fetchReviews(); }, [productId, vendorId, isVisible]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('vendor_id', vendorId);
      formData.append('product_id', productId);
      formData.append('product_type', productType);
      formData.append('rating', rating);
      formData.append('review_text', reviewText);
      if (photo) formData.append('photo', photo);

      if (editReview) {
        formData.delete('vendor_id');
        formData.delete('product_id');
        formData.delete('product_type');
        await axios.put(`${API_URL}/api/reviews/${editReview.id}`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Review updated');
      } else {
        await axios.post(`${API_URL}/api/reviews`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Review posted');
      }
      setShowForm(false);
      setEditReview(null);
      setRating(5);
      setReviewText('');
      setPhoto(null);
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Delete your review?')) return;
    try {
      await axios.delete(`${API_URL}/api/reviews/${reviewId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Review deleted');
      fetchReviews();
    } catch { toast.error('Delete failed'); }
  };

  const handleReply = async (reviewId) => {
    if (!replyText.trim()) return;
    try {
      const formData = new FormData();
      formData.append('reply_text', replyText);
      await axios.post(`${API_URL}/api/reviews/${reviewId}/reply`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Reply posted');
      setReplyingTo(null);
      setReplyText('');
      fetchReviews();
    } catch { toast.error('Reply failed'); }
  };

  const StarRating = ({ value, onChange, size = 'w-5 h-5' }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <button key={`star-${i}`} type="button" onClick={() => onChange?.(i)}
          className={`${size} ${i <= value ? 'text-amber-400' : 'text-slate-200'} ${onChange ? 'cursor-pointer hover:text-amber-300' : 'cursor-default'}`}>
          <svg fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="review-section" ref={containerRef}>
      {/* Summary */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-1">
          <StarRating value={Math.round(summary.average_rating)} size="w-4 h-4" />
          <span className="text-sm font-semibold text-slate-700">{summary.average_rating}</span>
        </div>
        <span className="text-xs text-slate-400">({summary.total_reviews} review{summary.total_reviews !== 1 ? 's' : ''})</span>
        {canReview && (
          <Button variant="outline" size="sm" className="h-6 px-2 text-[10px] ml-auto" onClick={() => { setShowForm(true); setEditReview(null); setRating(5); setReviewText(''); }} data-testid="write-review-btn">
            Write Review
          </Button>
        )}
      </div>

      {/* Review List */}
      {reviews.length > 0 && (
        <div className="space-y-3 max-h-[300px] overflow-y-auto">
          {reviews.map(r => (
            <div key={r.id} className="bg-slate-50 rounded-lg p-3 text-sm" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-700">{r.reviewer_name || 'User'}</span>
                  <Badge variant="outline" className="text-[9px]">{r.reviewer_role}</Badge>
                  <StarRating value={r.rating} size="w-3 h-3" />
                </div>
                <span className="text-[10px] text-slate-400">{new Date(r.created_at).toLocaleDateString('en-IN')}</span>
              </div>
              {r.review_text && <p className="text-xs text-slate-600 mb-1">{r.review_text}</p>}
              {r.photo_url && (
                <img src={`${API_URL}${r.photo_url}`} alt="Review" className="w-20 h-20 object-cover rounded border mb-1" />
              )}
              {r.vendor_reply && (
                <div className="mt-2 pl-3 border-l-2 border-violet-300 text-xs">
                  <span className="font-medium text-violet-700">Vendor Reply:</span>
                  <p className="text-slate-600">{r.vendor_reply}</p>
                </div>
              )}
              <div className="flex gap-2 mt-1">
                {canReview && (
                  <>
                    <button className="text-[10px] text-blue-600 hover:underline" onClick={() => { setEditReview(r); setRating(r.rating); setReviewText(r.review_text); setShowForm(true); }}>Edit</button>
                    <button className="text-[10px] text-red-500 hover:underline" onClick={() => handleDelete(r.id)}>Delete</button>
                  </>
                )}
                {canReply && !r.vendor_reply && (
                  <button className="text-[10px] text-violet-600 hover:underline" onClick={() => setReplyingTo(r.id)}>Reply</button>
                )}
              </div>
              {replyingTo === r.id && (
                <div className="flex gap-2 mt-2">
                  <Input value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write reply..." className="h-7 text-xs flex-1" />
                  <Button size="sm" className="h-7 text-xs" onClick={() => handleReply(r.id)}>Send</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setReplyingTo(null)}><X className="w-3 h-3" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Review Form Dialog */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!submitting) setShowForm(v); }}>
        <DialogContent className="max-w-sm" data-testid="review-form-dialog">
          <DialogHeader><DialogTitle>{editReview ? 'Edit Review' : 'Write a Review'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Rating</Label>
              <StarRating value={rating} onChange={setRating} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Review</Label>
              <textarea className="w-full border rounded-md p-2 text-sm min-h-[60px] focus:outline-none focus:ring-1 focus:ring-blue-300" value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Share your experience..." />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Photo (optional)</Label>
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setPhoto(e.target.files?.[0])}
                className="block w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-[10px] file:bg-blue-50 file:text-blue-700" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowForm(false)} disabled={submitting}>Cancel</Button>
            <Button size="sm" onClick={handleSubmit} disabled={submitting} data-testid="submit-review-btn">
              {submitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              {editReview ? 'Update' : 'Post Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
